import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HeroLogo } from "@/components/HeroLogo";
import { ChevronDown } from "lucide-react";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fadeUp = (reduce, delay) => ({
  initial: reduce ? { opacity: 1 } : { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const reduce = useReducedMotion();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative hoa-texture hoa-grain min-h-[100svh] flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Gold hairline frame — like the brand PDF (starts below the navbar) */}
      <div className="pointer-events-none absolute inset-x-3 sm:inset-x-5 bottom-3 sm:bottom-5 top-[4.6rem] sm:top-[5.4rem] border border-[rgba(179,145,74,0.25)] rounded-[4px] z-[1]" />

      <div className="relative z-[2] mx-auto w-full max-w-[1120px] px-5 sm:px-6 flex flex-col items-center text-center pt-24 pb-20">
        <motion.div {...fadeUp(reduce, 0.1)}>
          <HeroLogo />
        </motion.div>

        <motion.h1
          {...fadeUp(reduce, 0.45)}
          className="font-display text-[var(--hoa-cream-50)] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-10"
          data-testid="hero-wordmark"
        >
          house of appetite
        </motion.h1>

        <motion.p
          {...fadeUp(reduce, 0.62)}
          className="font-display hoa-gold-text text-2xl sm:text-3xl mt-5"
          data-testid="hero-tagline"
        >
          give in.
        </motion.p>

        <motion.p
          {...fadeUp(reduce, 0.78)}
          className="font-label text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-[#93a796] mt-7"
        >
          Artisanal Home Baking&nbsp;&nbsp;•&nbsp;&nbsp;Small Batch
        </motion.p>

        <motion.div {...fadeUp(reduce, 0.94)} className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 mt-10 w-full sm:w-auto">
          <button
            data-testid="hero-secondary-cta-button"
            onClick={() => scrollToId("story")}
            className="w-full sm:w-auto font-label text-[12px] tracking-[0.2em] uppercase h-12 px-8 inline-flex items-center justify-center rounded-full bg-[var(--hoa-cream-50)] text-[var(--hoa-ink-950)] border border-[rgba(179,145,74,0.45)] shadow-[0_10px_30px_rgba(0,0,0,0.22)] hover:bg-[var(--hoa-cream-100)] hover:-translate-y-[1px] active:scale-[0.98] transition-[background-color,transform,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(179,145,74,0.55)]"
          >
            My Story
          </button>
        </motion.div>
      </div>

      <motion.button
        {...fadeUp(reduce, 1.3)}
        onClick={() => scrollToId("story")}
        aria-label="Scroll down"
        data-testid="hero-scroll-cue"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[2] text-[rgba(240,233,216,0.6)]"
      >
        <ChevronDown className="hoa-scroll-cue w-6 h-6" strokeWidth={1.4} />
      </motion.button>
    </section>
  );
};
